import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from "react";
import Axios from "axios";

import { API } from "@api";
import { Nullable, UserByEmail } from "@utils";
import { logout } from "./ciam";

export const LOCAL_STORAGE_KEY = "wfp-gas/session";

export type Session = {
  user: UserByEmail;
  accessToken: string;
  refreshToken: string;
};

export interface IAuthenticationContext {
  signIn: (token: string, refreshToken: string, email: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  isUserInactive: boolean;
  session: Nullable<Session>;
  currentUser: Nullable<UserByEmail>;
}

const setAuthenticationHeader = (token: string) => {
  if (token) {
    Axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }
};

const removeAuthenticationHeader = () => {
  delete Axios.defaults.headers.common["Authorization"];
};

const DEFAULT_CONTEXT_VALUE: IAuthenticationContext = {
  async signIn(_token: string, _email: string) {},
  async logout() {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  },
  isAuthenticated: false,
  isUserInactive: false,
  session: null,
  currentUser: null,
};

export const AuthenticationContext = createContext(DEFAULT_CONTEXT_VALUE);
export const AuthenticationContextProvider = AuthenticationContext.Provider;

export const AuthenticationProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isUserInactive, setUserInactive] = useState<boolean>(false);
  const [session, setSession] = useState<Nullable<Session>>(() => {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Set Axios header immediately so it's ready for the first render's API calls
        setAuthenticationHeader(parsed?.accessToken);
        return parsed;
      } catch {
        return null;
      }
    }
    return null;
  });

  const currentUser = useMemo(() => (session ? session.user : null), [session]);

  const updateSession = (session: Nullable<Session>) => {
    setSession(session);
    if (session?.accessToken) {
      setAuthenticationHeader(session.accessToken);
    } else {
      removeAuthenticationHeader();
    }
  };

  const setAuthenticatedUser = useCallback(
    async (token: string, refreshToken: string, email: string) => {
      try {
        const user = await API.users.byEmail(email);

        const session = {
          accessToken: token!,
          user,
          refreshToken,
        };
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(session));
        updateSession(session || null);
        setUserInactive(false);
      } catch (error) {
        updateSession(null);
        if ((error as Error).message.includes("401")) {
          setUserInactive(true);
        }
      }
    },
    [],
  );

  const removeAuthenticatedUser = useCallback(() => {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    updateSession(null);
  }, []);

  const value = useMemo(
    () => ({
      ...DEFAULT_CONTEXT_VALUE,
      signIn: async (token: string, refreshToken: string, email: string) => {
        try {
          setAuthenticationHeader(token);
          return setAuthenticatedUser(token, refreshToken, email);
        } catch (error) {
          // TODO: Handle errors
          console.log(error);
        }
      },
      logout: async () => {
        try {
          removeAuthenticatedUser();
          logout();
        } catch (error) {
          // TODO: Handle errors
          console.log(error);
        }
      },
      session,
      isAuthenticated: Boolean(session),
      isUserInactive,
      currentUser,
    }),
    [session, currentUser, isUserInactive],
  );

  return (
    <AuthenticationContextProvider value={value}>
      {children}
    </AuthenticationContextProvider>
  );
};
